import React from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import ProgressBar from 'react-bootstrap/ProgressBar'
import { getYouTubeEmbedSrc } from '../../../utils/youtube'

const TAB_OPTIONS = [
    { value: 'producto', label: 'Producto' },
    { value: 'llenado', label: 'Llenado' },
    { value: 'condiciones', label: 'Condiciones' },
    { value: 'recursos', label: 'Recursos (PDF)' },
    { value: 'videos_adicionales', label: 'Videos adicionales' },
]

const MAX_VIDEO_MB = 200

const ItemForm = ({ section, item, videoUrl, sectionOptions = [] }) => {
    const { flash, errors: pageErrors } = usePage().props
    const [saving, setSaving] = React.useState(false)
    const isEdit = Boolean(item)
    const options = sectionOptions
    const rootOption = options.find((o) => o.is_root) || null
    const currentSectionId = item?.sia_section_id || section.id
    const initialSectionId = options.some((o) => Number(o.id) === Number(currentSectionId))
        ? Number(currentSectionId)
        : rootOption
          ? Number(rootOption.id)
          : ''

    const defaultTab = () => {
        if (item?.tab) return item.tab
        if (section.layout_type === 'product_tabs') return 'producto'
        return ''
    }

    const defaultVideoSource = () => {
        if (item?.video_status) return 'mp4'
        return 'youtube'
    }

    const form = useForm({
        sia_section_id: initialSectionId,
        tab: defaultTab(),
        type: item?.type || 'video',
        video_source: defaultVideoSource(),
        title: item?.title || '',
        description: item?.description || '',
        youtube_url: item?.youtube_url || '',
        author: item?.author || '',
        duration_label: item?.duration_label || '',
        main_topic: item?.main_topic || '',
        highlights: item?.highlights || '',
        application_text: item?.application_text || '',
        download_url: item?.download_url || '',
        order: item?.order ?? 0,
        file: null,
        video: null,
    })

    const fieldError = (key) => {
        const e = form.errors[key] || pageErrors?.[key]
        return Array.isArray(e) ? e[0] : e
    }

    const selectedSection = options.find((o) => Number(o.id) === Number(form.data.sia_section_id)) || options[0] || section
    const layoutType = selectedSection?.layout_type || section.layout_type
    const isProductTabs = layoutType === 'product_tabs'
    const isLessonList = layoutType === 'lesson_list'
    const allowsMp4 = ['sidebar_videos', 'video_grid', 'media_catalog', 'lesson_list', 'product_tabs'].includes(layoutType)
    const allowsTypeChoice =
        isLessonList ||
        layoutType === 'sidebar_videos' ||
        layoutType === 'video_grid' ||
        layoutType === 'media_catalog'

    const isDocument = allowsTypeChoice
        ? form.data.type === 'document'
        : isProductTabs && form.data.tab === 'recursos'

    const showVideoSource = allowsMp4 && !isDocument && (allowsTypeChoice ? form.data.type === 'video' : true)
    const isMp4 = showVideoSource && form.data.video_source === 'mp4'

    const basePayload = () => {
        const payload = {
            sia_section_id: form.data.sia_section_id === '' || form.data.sia_section_id === null ? '' : Number(form.data.sia_section_id),
            title: form.data.title,
            description: form.data.description ?? '',
            youtube_url: form.data.youtube_url ?? '',
            video_source: form.data.video_source,
            author: form.data.author ?? '',
            duration_label: form.data.duration_label ?? '',
            main_topic: form.data.main_topic ?? '',
            highlights: form.data.highlights ?? '',
            application_text: form.data.application_text ?? '',
            download_url: form.data.download_url ?? '',
            order: Number(form.data.order),
        }
        if (isProductTabs) payload.tab = form.data.tab
        if (allowsTypeChoice) payload.type = form.data.type
        return payload
    }

    const submit = (e) => {
        e.preventDefault()
        if (isMp4 && form.data.video && form.data.video.size > MAX_VIDEO_MB * 1024 * 1024) {
            form.setError('video', `El video no puede superar ${MAX_VIDEO_MB} MB.`)
            return
        }

        const needsFormData = Boolean(form.data.file || form.data.video)

        if (isEdit) {
            if (needsFormData) {
                Inertia.post(
                    route('sia.manage.items.update', item.id),
                    { _method: 'put', ...basePayload(), file: form.data.file, video: form.data.video },
                    {
                        forceFormData: true,
                        preserveScroll: true,
                        onStart: () => setSaving(true),
                        onFinish: () => setSaving(false),
                    }
                )
                return
            }
            Inertia.put(route('sia.manage.items.update', item.id), basePayload(), {
                preserveScroll: true,
                onStart: () => setSaving(true),
                onFinish: () => setSaving(false),
            })
            return
        }
        form.post(route('sia.manage.items.store', section.slug), { forceFormData: true })
    }

    const path = isEdit ? `/admin/sia/manage/items/${item.id}/edit` : `/admin/sia/manage/sections/${section.slug}/items/create`
    const backListUrl = route('sia.manage.section.items', section.slug)
    const uploadPct = form.progress?.percentage

    return (
        <>
            <div className="mb-5 mt-5">
                <Breadcrumbs currentPath={path} />
            </div>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-6">
                <h1 className="fs-2 fw-bold text-gray-900 mb-0">
                    {isEdit ? `Editar contenido · ${section.title}` : `Nuevo contenido · ${section.title}`}
                </h1>
                <Link href={backListUrl} className="btn btn-sm btn-light">
                    ← Volver al listado
                </Link>
            </div>

            {flash.success && <div className="alert alert-success mb-5">{flash.success}</div>}
            {flash.error && <div className="alert alert-danger mb-5">{flash.error}</div>}
            {item?.video_status === 'failed' && (
                <div className="alert alert-warning mb-5">
                    Error al procesar el video{item.video_error ? `: ${item.video_error}` : '.'} Puedes subir otro MP4.
                </div>
            )}

            <Card>
                <Card.Body>
                    <p className="text-muted mb-4">
                        Sección: <strong>{section.title}</strong>
                        {allowsTypeChoice && (
                            <span className="ms-2">
                                — Puedes publicar <strong>video</strong> (YouTube o MP4 hasta {MAX_VIDEO_MB} MB) o un{' '}
                                <strong>PDF</strong> (visor + descarga).
                            </span>
                        )}
                        {!allowsTypeChoice && allowsMp4 && !isDocument && (
                            <span className="ms-2">— Puedes publicar YouTube o un MP4 (hasta {MAX_VIDEO_MB} MB).</span>
                        )}
                    </p>
                    <Form onSubmit={submit}>
                        <Row className="g-3 mb-4">
                            <Col md={8}>
                                <Form.Label>Subcategoría</Form.Label>
                                {options.length === 0 ? (
                                    <div className="alert alert-warning mb-0">
                                        Crea una subcategoría antes de agregar un archivo.
                                    </div>
                                ) : (
                                    <Form.Select
                                        value={form.data.sia_section_id}
                                        onChange={(e) => form.setData('sia_section_id', e.target.value === '' ? '' : Number(e.target.value))}
                                    >
                                        {options.map((o) => (
                                            <option key={o.id} value={o.id}>
                                                {o.title}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                                {fieldError('sia_section_id') && (
                                    <div className="text-danger small">{fieldError('sia_section_id')}</div>
                                )}
                                <Form.Text muted>
                                    Si no eliges categoría, el archivo se guarda en la raíz. También puedes elegir la opción que dice raíz.
                                </Form.Text>
                            </Col>
                        </Row>
                        {isProductTabs && (
                            <Row className="g-3 mb-4">
                                <Col md={6}>
                                    <Form.Label>Pestaña</Form.Label>
                                    <Form.Select value={form.data.tab} onChange={(e) => form.setData('tab', e.target.value)} required>
                                        {TAB_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            </Row>
                        )}
                        {allowsTypeChoice && (
                            <Row className="g-3 mb-4">
                                <Col md={6}>
                                    <Form.Label>Tipo de contenido</Form.Label>
                                    <Form.Select value={form.data.type} onChange={(e) => form.setData('type', e.target.value)} required>
                                        <option value="video">Video (YouTube o MP4)</option>
                                        <option value="document">Documento (PDF o Excel)</option>
                                    </Form.Select>
                                    <Form.Text muted>
                                        En Bienvenido, Cultura, Comisiones, Producto, Formatos y Formación se suben PDF o Excel. El Excel se descarga; no se abre en el visor.
                                    </Form.Text>
                                </Col>
                            </Row>
                        )}
                        <Row className="g-3 mb-3">
                            <Col md={8}>
                                <Form.Label>Nombre / título</Form.Label>
                                <Form.Control value={form.data.title} onChange={(e) => form.setData('title', e.target.value)} required />
                                {fieldError('title') && <div className="text-danger small">{fieldError('title')}</div>}
                            </Col>
                            <Col md={4}>
                                <Form.Label>Orden</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={form.data.order}
                                    onChange={(e) => form.setData('order', parseInt(e.target.value, 10) || 0)}
                                />
                            </Col>
                        </Row>

                        {showVideoSource && (
                            <Row className="g-3 mb-3">
                                <Col md={12}>
                                    <Form.Label className="d-block">Fuente del video</Form.Label>
                                    <Form.Check
                                        inline
                                        type="radio"
                                        name="video_source"
                                        id="vs-youtube"
                                        label="YouTube"
                                        checked={form.data.video_source === 'youtube'}
                                        onChange={() => form.setData('video_source', 'youtube')}
                                    />
                                    <Form.Check
                                        inline
                                        type="radio"
                                        name="video_source"
                                        id="vs-mp4"
                                        label="MP4"
                                        checked={form.data.video_source === 'mp4'}
                                        onChange={() => form.setData('video_source', 'mp4')}
                                    />
                                </Col>
                            </Row>
                        )}

                        {!isDocument && !isMp4 && (
                            <Row className="g-3 mb-3">
                                <Col md={12}>
                                    <Form.Label>URL de YouTube</Form.Label>
                                    <Form.Control
                                        value={form.data.youtube_url}
                                        onChange={(e) => form.setData('youtube_url', e.target.value)}
                                        required={!isLessonList}
                                    />
                                    {fieldError('youtube_url') && <div className="text-danger small">{fieldError('youtube_url')}</div>}
                                    {form.data.youtube_url && getYouTubeEmbedSrc(form.data.youtube_url) && (
                                        <div className="ratio ratio-16x9 mt-3 rounded overflow-hidden border" style={{ maxWidth: 480 }}>
                                            <iframe
                                                title="Vista previa"
                                                src={getYouTubeEmbedSrc(form.data.youtube_url)}
                                                className="border-0"
                                                allowFullScreen
                                            />
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        )}

                        {!isDocument && isMp4 && (
                            <Row className="g-3 mb-3">
                                <Col md={12}>
                                    <Form.Label>
                                        {isEdit && item?.video_status === 'ready'
                                            ? 'Reemplazar MP4 (opcional)'
                                            : `Archivo MP4 (máx. ${MAX_VIDEO_MB} MB)`}
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept="video/mp4,video/x-m4v,video/quicktime,.mp4"
                                        onChange={(e) => form.setData('video', e.target.files[0] || null)}
                                        required={!isEdit || item?.video_status === 'failed'}
                                    />
                                    {fieldError('video') && <div className="text-danger small">{fieldError('video')}</div>}
                                    {item?.video_original_filename && (
                                        <div className="text-muted small mt-1">Actual: {item.video_original_filename}</div>
                                    )}
                                    {typeof uploadPct === 'number' && (
                                        <ProgressBar className="mt-3" now={uploadPct} label={`${uploadPct}%`} />
                                    )}
                                    {videoUrl && (
                                        <div className="mt-4">
                                            <div className="text-muted fs-7 mb-2">Vista previa</div>
                                            <video
                                                src={videoUrl}
                                                controls
                                                controlsList="nodownload"
                                                className="w-100 rounded border"
                                                style={{ maxHeight: 360 }}
                                            />
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        )}

                        <>
                                <Row className="g-3 mb-3">
                                    <Col md={6}>
                                        <Form.Label>Autor</Form.Label>
                                        <Form.Control value={form.data.author} onChange={(e) => form.setData('author', e.target.value)} />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label>Duración</Form.Label>
                                        <Form.Control
                                            value={form.data.duration_label}
                                            onChange={(e) => form.setData('duration_label', e.target.value)}
                                            placeholder="ej. aprox. 20–30 min"
                                        />
                                    </Col>
                                </Row>
                                <Row className="g-3 mb-3">
                                    <Col md={12}>
                                        <Form.Label>Tema principal</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={6}
                                            style={{ minHeight: 140 }}
                                            value={form.data.main_topic}
                                            onChange={(e) => form.setData('main_topic', e.target.value)}
                                        />
                                    </Col>
                                </Row>
                                <Row className="g-3 mb-3">
                                    <Col md={12}>
                                        <Form.Label>Puntos a resaltar</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={12}
                                            style={{ minHeight: 260 }}
                                            value={form.data.highlights}
                                            onChange={(e) => form.setData('highlights', e.target.value)}
                                            placeholder="Un punto por línea"
                                        />
                                    </Col>
                                </Row>
                                <Row className="g-3 mb-3">
                                    <Col md={12}>
                                        <Form.Label>Aplicación en Yireh</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={12}
                                            style={{ minHeight: 260 }}
                                            value={form.data.application_text}
                                            onChange={(e) => form.setData('application_text', e.target.value)}
                                        />
                                    </Col>
                                </Row>
                                <Row className="g-3 mb-3">
                                    <Col md={12}>
                                        <Form.Label>URL de descarga externa (opcional)</Form.Label>
                                        <Form.Control
                                            value={form.data.download_url}
                                            onChange={(e) => form.setData('download_url', e.target.value)}
                                        />
                                    </Col>
                                </Row>
                        </>

                        <Row className="g-3 mb-3">
                            <Col md={12}>
                                <Form.Label>Descripción (opcional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    style={{ minHeight: 120 }}
                                    value={form.data.description}
                                    onChange={(e) => form.setData('description', e.target.value)}
                                />
                            </Col>
                        </Row>

                        {isDocument && (
                            <Row className="g-3 mb-4">
                                <Col md={12}>
                                    <Form.Label>{isEdit ? 'Reemplazar archivo (opcional)' : 'Archivo PDF o Excel (requerido)'}</Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept=".pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                        onChange={(e) => form.setData('file', e.target.files[0] || null)}
                                    />
                                    {isEdit && item.file_path && <div className="text-muted small mt-1">Actual: {item.file_path}</div>}
                                    {fieldError('file') && <div className="text-danger small">{fieldError('file')}</div>}
                                </Col>
                            </Row>
                        )}

                        {isLessonList && !isDocument && (
                            <Row className="g-3 mb-4">
                                <Col md={12}>
                                    <Form.Label>PDF o Excel opcional (reto / material)</Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept=".pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                        onChange={(e) => form.setData('file', e.target.files[0] || null)}
                                    />
                                    {isEdit && item.file_path && <div className="text-muted small mt-1">Actual: {item.file_path}</div>}
                                    {fieldError('file') && <div className="text-danger small">{fieldError('file')}</div>}
                                </Col>
                            </Row>
                        )}

                        <Button type="submit" variant="primary" disabled={form.processing || saving}>
                            {isEdit ? 'Guardar cambios' : 'Crear'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </>
    )
}

ItemForm.layout = (page) => <Layout title={page.props.item ? 'SIA · Editar contenido' : 'SIA · Nuevo contenido'}>{page}</Layout>

export default ItemForm
